/**
 * Crisp, minimal unit symbols (screen-space strokes, css px), after the mockup:
 *   radar ◎  SAM ^  silo ▣  city ■ (one block per level)  airbase ✕  sub ⬭  carrier ⏢  fighter/bomber ➤
 */
import type { EntityKind } from '@sa/sim';
import type { RGB } from '../theme';
import type { SegStyle, VectorBatch } from './vector';

export interface GlyphOpts {
  /** radians (map space) for aircraft / ships */
  heading?: number | null;
  /** city level */
  level?: number;
  scale?: number;
  dashed?: boolean;
  glow?: number;
}

type Pt = readonly [number, number];

/** In-world glyph size relative to the build-strip icons (legible at the default zoom). */
const GLYPH_SCALE = 1.2;

function poly(vb: VectorBatch, x: number, y: number, pts: readonly Pt[], closed: boolean, col: RGB, a: number, s: SegStyle, k: number, rot: number): void {
  const c = Math.cos(rot);
  const sn = Math.sin(rot);
  const n = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < n; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    vb.glyph(x, y, (p[0] * c - p[1] * sn) * k, (p[0] * sn + p[1] * c) * k, (q[0] * c - q[1] * sn) * k, (q[0] * sn + q[1] * c) * k, col, a, s);
  }
}

function ellipse(rx: number, ry: number, n: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    out.push([Math.cos(t) * rx, Math.sin(t) * ry]);
  }
  return out;
}

const SAM: Pt[] = [
  [-5, 4],
  [0, -5],
  [5, 4],
];
const SILO: Pt[] = [
  [-4, -4],
  [4, -4],
  [4, 4],
  [-4, 4],
];
/**
 * A city is one small block per level, in a fixed cluster, so its level reads at a glance:
 * L1 ■, L2 a pair, L3 a pyramid, L4 a 2×2, L5 a plus (centres in glyph units; `CITY_BLOCK` half-size).
 * The gaps (≈ 2.9 css px) are wide enough that bloom does not melt the blocks into one blob.
 */
export const CITY_BLOCKS: readonly (readonly Pt[])[] = [
  [[0, 0]],
  [[-2.5, 0], [2.5, 0]],
  [[-2.5, 2.3], [2.5, 2.3], [0, -2.3]],
  [[-2.5, -2.5], [2.5, -2.5], [-2.5, 2.5], [2.5, 2.5]],
  [[0, 0], [-5, 0], [5, 0], [0, -5], [0, 5]],
];
const CITY_BLOCK = 1.3;

const SUB = ellipse(7, 2.6, 12);
const SUB_SAIL: Pt[] = [
  [-1.5, -2.4],
  [-1.5, -5],
  [1.5, -5],
  [1.5, -2.4],
];
const CARRIER: Pt[] = [
  [-8, -1],
  [8, -1],
  [6, 3],
  [-6, 3],
];
const CARRIER_ISLAND: Pt[] = [
  [2, -1],
  [2, -5],
  [4, -5],
  [4, -1],
];
const FIGHTER: Pt[] = [
  [6, 0],
  [-4, -4.5],
  [-1.5, 0],
  [-4, 4.5],
];
const BOMBER: Pt[] = [
  [7, 0],
  [-5, -6],
  [-2, 0],
  [-5, 6],
];

export function drawGlyph(vb: VectorBatch, kind: EntityKind, x: number, y: number, col: RGB, a: number, o: GlyphOpts = {}): void {
  const k = o.scale ?? GLYPH_SCALE;
  const s: SegStyle = { width: 1.5, glow: o.glow ?? 0, ...(o.dashed ? { dash: [1.5, 2] as [number, number] } : {}) };
  switch (kind) {
    case 'radar':
      vb.ring(x, y, 5 * k, col, a, { width: 1.5, px: true, glow: o.glow ?? 0, ...(o.dashed ? { dash: [1.5, 2] as [number, number] } : {}) });
      vb.disc(x, y, 1.1 * k, col, a);
      break;
    case 'sam':
      poly(vb, x, y, SAM, false, col, a, s, k, 0);
      break;
    case 'silo':
      poly(vb, x, y, SILO, true, col, a, s, k, 0);
      vb.disc(x, y, 1.5 * k, col, a);
      break;
    case 'city': {
      const blocks = CITY_BLOCKS[Math.max(1, Math.min(CITY_BLOCKS.length, Math.round(o.level ?? 1))) - 1];
      const h = CITY_BLOCK;
      for (const [bx, by] of blocks) {
        // under construction / a ghost: outlines; else a filled block
        if (o.dashed) poly(vb, x, y, [[bx - h, by - h], [bx + h, by - h], [bx + h, by + h], [bx - h, by + h]], true, col, a, { width: 1 }, k, 0);
        else vb.square(x, y, h * k, col, a, o.glow ?? 0, bx * k, by * k);
      }
      break;
    }
    case 'airbase':
      vb.glyph(x, y, -6 * k, 3 * k, 6 * k, -3 * k, col, a, s);
      vb.glyph(x, y, -3 * k, -4 * k, 3 * k, 4 * k, col, a, { ...s, width: 1 });
      break;
    case 'sub':
      poly(vb, x, y, SUB, true, col, a, { ...s, width: 1.2 }, k, 0);
      poly(vb, x, y, SUB_SAIL, false, col, a, { ...s, width: 1.2 }, k, 0);
      break;
    case 'carrier':
      poly(vb, x, y, CARRIER, true, col, a, { ...s, width: 1.2 }, k, 0);
      poly(vb, x, y, CARRIER_ISLAND, false, col, a, { ...s, width: 1.2 }, k, 0);
      break;
    case 'fighter':
      poly(vb, x, y, FIGHTER, true, col, a, { ...s, width: 1.2 }, k * 0.9, o.heading ?? 0);
      break;
    case 'bomber':
      poly(vb, x, y, BOMBER, true, col, a, { ...s, width: 1.3 }, k, o.heading ?? 0);
      break;
    case 'bomb':
      vb.disc(x, y, 1.8 * k, col, a, 4);
      break;
    default:
      vb.disc(x, y, 1.5 * k, col, a);
  }
}

/** Glyph for the build strip / placement ghost, drawn into a 2D canvas (same shapes). */
export function drawGlyph2d(ctx: CanvasRenderingContext2D, kind: EntityKind, x: number, y: number, k: number): void {
  const path = (pts: readonly Pt[], closed: boolean) => {
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(x + p[0] * k, y + p[1] * k) : ctx.moveTo(x + p[0] * k, y + p[1] * k)));
    if (closed) ctx.closePath();
    ctx.stroke();
  };
  ctx.lineWidth = 1.4;
  switch (kind) {
    case 'radar':
      ctx.beginPath();
      ctx.arc(x, y, 5 * k, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillRect(x - 1, y - 1, 2, 2);
      break;
    case 'sam':
      path(SAM, false);
      break;
    case 'silo':
      path(SILO, true);
      ctx.fillRect(x - 1.5 * k, y - 1.5 * k, 3 * k, 3 * k);
      break;
    case 'city':
      ctx.fillRect(x - 3 * k, y - 3 * k, 6 * k, 6 * k);
      break;
    case 'airbase':
      path(
        [
          [-6, 3],
          [6, -3],
        ],
        false,
      );
      path(
        [
          [-3, -4],
          [3, 4],
        ],
        false,
      );
      break;
    case 'sub':
      path(SUB, true);
      path(SUB_SAIL, false);
      break;
    case 'carrier':
      path(CARRIER, true);
      path(CARRIER_ISLAND, false);
      break;
    case 'fighter':
      path(FIGHTER, true);
      break;
    case 'bomber':
      path(BOMBER, true);
      break;
    default:
      ctx.fillRect(x - 1.5, y - 1.5, 3, 3);
  }
}
